import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import type { Toast, ToastType } from "../../types/toast";

const EXIT_MS = 300;

const STYLES: Record<ToastType, { border: string; icon: string; bar: string; Icon: typeof Info }> = {
  success: { border: "border-l-green-500", icon: "text-green-600", bar: "bg-green-500", Icon: CheckCircle2 },
  error: { border: "border-l-red-500", icon: "text-red-600", bar: "bg-red-500", Icon: XCircle },
  info: { border: "border-l-blue-500", icon: "text-blue-600", bar: "bg-blue-500", Icon: Info },
};

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const { border, icon, bar, Icon } = STYLES[toast.type];
  const [shown, setShown] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef(toast.duration);
  const startRef = useRef(0);
  const timerRef = useRef(0);

  const close = useCallback(() => {
    window.clearTimeout(timerRef.current);
    setExiting(true);
    window.setTimeout(() => onDismiss(toast.id), EXIT_MS);
  }, [onDismiss, toast.id]);

  const startTimer = useCallback(() => {
    startRef.current = performance.now();
    timerRef.current = window.setTimeout(close, remainingRef.current);
  }, [close]);

  useEffect(() => {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setShown(true));
    });
    startTimer();
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(timerRef.current);
    };
  }, [startTimer]);

  function pause() {
    if (exiting) return;
    window.clearTimeout(timerRef.current);
    remainingRef.current -= performance.now() - startRef.current;
    setPaused(true);
  }

  function resume() {
    if (exiting) return;
    setPaused(false);
    startTimer();
  }

  const visible = shown && !exiting;

  return (
    <div
      role={toast.type === "error" ? "alert" : "status"}
      onMouseEnter={pause}
      onMouseLeave={resume}
      className={`pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-lg border border-gray-200 border-l-4 bg-white px-4 py-3 shadow-lg transition-all duration-300 ease-out ${border} ${visible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
    >
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${icon}`} aria-hidden="true" />
      <p className="flex-1 text-sm text-gray-800">{toast.message}</p>
      <button
        type="button"
        onClick={close}
        className="shrink-0 rounded p-0.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <span
        className={`absolute bottom-0 left-0 h-0.5 w-full origin-left ${bar}`}
        style={{ animation: `toast-progress ${toast.duration}ms linear forwards`, animationPlayState: paused ? "paused" : "running" }}
        aria-hidden="true"
      />
    </div>
  );
}

export default function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}) {
  if (!toasts.length) return null;

  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
